import { useMemo } from "react";

import { useSessionUser, useWorkspaceConnections } from "@web-app/api";
import { Order, Route } from "@web-app/types";

interface WorkspaceEntry {
  avatarUrl: string | null;
  id: string;
  name: string;
  orders: Order[];
  routes: Route[];
}

export default function useConnectionsView({
  unroutedOrders,
  routes,
}: {
  routes: Route[];
  unroutedOrders: Order[];
}) {
  const { data: connections, isFetching: isWorkspaceConnectionsLoading } =
    useWorkspaceConnections();
  const { data: currentUser } = useSessionUser();

  const ordersGroupedByWorkspace = useMemo(() => {
    const allTasks = [...unroutedOrders, ...routes];

    const workspaceWithConnections = allTasks.reduce((workspaces, task) => {
      let workspace = currentUser?.currentWorkspace;

      if (connections?.length) {
        const connection = connections?.find(
          (conn) =>
            conn.workspaceId == task.workspaceId ||
            conn.connectedWorkspaceId == task.workspaceId,
        );
        workspace =
          connection?.workspaceId == task.workspaceId
            ? connection.workspace
            : connection?.connectedWorkspace;
      }

      if (!workspace) return workspaces;

      let workspaceEntry = workspaces.find((w) => w.id === workspace.id);
      if (!workspaceEntry) {
        workspaceEntry = {
          avatarUrl: workspace.avatarUrl,
          id: workspace.id,
          name: workspace.name,
          orders: [],
          routes: [],
        };
        workspaces.push(workspaceEntry);
      }

      if (!("ordersOrder" in task)) {
        workspaceEntry.orders.push(task as Order);
      } else {
        workspaceEntry.routes.push(task as Route);
      }

      return workspaces;
    }, [] as WorkspaceEntry[]);

    return workspaceWithConnections;
  }, [unroutedOrders, routes]);

  return {
    connections: connections ?? [],
    isWorkspaceConnectionsLoading,
    ordersGroupedByWorkspace,
  };
}
